import React, { useMemo } from "react";
import { GuitarNeck, type NeckConnection } from "../GuitarNeck";
import { PianoKeyboard } from "../piano/PianoKeyboard";
import { PianoPracticePanel } from "../piano/PianoPracticePanel";
import type { NeckPosition, NoteName } from "../../lib/guitarEngine";
import { useInstrument } from "../../contexts/InstrumentContext";
import { isFretboardInstrument } from "../../lib/instrumentEngine";
import {
  neckPositionsToKeyboardFingering,
  activeNoteToMidi,
  findNeckPositionForMidi,
  midiToNoteAtPosition,
} from "../../lib/practiceSurface";

export type { NeckConnection };

export interface PracticeSurfaceProps {
  highlightedPositions?: NeckPosition[];
  mutedStrings?: number[];
  activeNote?: { string: number; fret: number } | null;
  onFretClick?: (stringNum: number, fret: number, note: NoteName) => void;
  onFretHover?: (stringNum: number, fret: number, note: NoteName) => void;
  onFretLeave?: () => void;
  showNoteNames?: boolean;
  showDegrees?: boolean;
  interactive?: boolean;
  stringOrder?: "tab" | "physical";
  showFingerBadges?: boolean;
  showMappedKeyLabels?: boolean;
  hand?: "left" | "right";
  hideFretZero?: boolean;
  leftHanded?: boolean;
  barre?: { fromString: number; toString: number; fret: number } | null;
  connections?: NeckConnection[];
  dimmedKeys?: Set<string>;
  focusLevel?: 1 | 2 | 3;
  practiceFeedback?: "success" | "fail";
  successPositions?: Set<string>;
  groupedPositions?: Map<string, { speciesFill: string; groupHalos: string[] }>;
  visualStyle?: "warm" | "lutherie" | "wireframe";
  sizeTier?: "default" | "monumental";
  boardWidthOverride?: number;
  spotlightKeys?: Set<string>;
  hoverNote?: { string: number; fret: number } | null;
  dimInactiveDuringPlayback?: boolean;
  chordNutTypography?: boolean;
  exploreChordNodes?: boolean;
  sparseCells?: boolean;
  className?: string;
}

/**
 * Superficie de práctica unificada: mástil o teclado según instrumento de sesión.
 */
export const PracticeSurface: React.FC<PracticeSurfaceProps> = (props) => {
  const {
    highlightedPositions = [],
    activeNote,
    onFretClick,
    onFretHover,
    onFretLeave,
    className = "",
    visualStyle = "lutherie",
    sizeTier = "default",
    boardWidthOverride,
    spotlightKeys,
    hoverNote,
    dimInactiveDuringPlayback,
    chordNutTypography,
    exploreChordNodes,
    sparseCells,
    ...neckProps
  } = props;

  const { instrument, surface } = useInstrument();
  const fretRef = isFretboardInstrument(instrument) ? instrument : "guitar";

  const keyboardFingering = useMemo(
    () => neckPositionsToKeyboardFingering(highlightedPositions, fretRef),
    [highlightedPositions, fretRef]
  );

  const activeMidi = useMemo(
    () => activeNoteToMidi(activeNote, fretRef),
    [activeNote, fretRef]
  );

  if (surface === "keyboard") {
    return (
      <PianoPracticePanel className={`h-full ${className}`}>
        <PianoKeyboard
          fingering={keyboardFingering}
          activeMidi={activeMidi}
          showFingerRings={props.showFingerBadges !== false}
          showDegreeFill
          showMappedLabels={props.showMappedKeyLabels}
          dimUnmapped={false}
          embedded
          showHandZones={false}
          onKeyClick={
            props.interactive !== false && onFretClick
              ? (midi) => {
                  const match = findNeckPositionForMidi(highlightedPositions, midi, fretRef);
                  if (match) {
                    onFretClick(match.string, match.fret, match.note);
                    return;
                  }
                  const { string, fret, note } = midiToNoteAtPosition(midi);
                  onFretClick(string, fret, note);
                }
              : undefined
          }
        />
      </PianoPracticePanel>
    );
  }

  return (
    <div className={`w-full h-full min-h-0 max-w-none flex items-stretch justify-center ${className}`}>
      <GuitarNeck
        highlightedPositions={highlightedPositions}
        activeNote={activeNote}
        onFretClick={onFretClick}
        onFretHover={onFretHover}
        onFretLeave={onFretLeave}
        visualStyle={visualStyle}
        sizeTier={sizeTier}
        boardWidthOverride={boardWidthOverride}
        spotlightKeys={spotlightKeys}
        hoverNote={hoverNote}
        dimInactiveDuringPlayback={dimInactiveDuringPlayback}
        chordNutTypography={chordNutTypography}
        exploreChordNodes={exploreChordNodes}
        sparseCells={sparseCells}
        {...neckProps}
      />
    </div>
  );
};
